import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, PlusCircle, Pencil, Trash2, Users, Building2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", contact_email: "", contact_phone: "", address: "", logo_url: "", notes: "", active: true };

export default function SponsorOrgs() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("sponsor_orgs.manage");
  const canAssign = hasPerm("users.update");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // sponsor obj when editing, {} for new
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);   // sponsor obj
  const [assigned, setAssigned] = useState(new Set());
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, uRes] = await Promise.all([
        api.get(`/sponsor-orgs?include_inactive=${includeInactive}`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setItems(sRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setEditing({}); };
  const openEdit = (s) => {
    setForm({
      name: s.name || "",
      contact_email: s.contact_email || "",
      contact_phone: s.contact_phone || "",
      address: s.address || "",
      logo_url: s.logo_url || "",
      notes: s.notes || "",
      active: s.active !== false,
    });
    setEditing(s);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (editing && editing.id) {
        await api.put(`/sponsor-orgs/${editing.id}`, form);
        toast.success("Sponsor org updated");
      } else {
        await api.post("/sponsor-orgs", form);
        toast.success("Sponsor org created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor org "${s.name}"?`)) return;
    try {
      await api.delete(`/sponsor-orgs/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const openAssign = async (s) => {
    setAssignOpen(s);
    try {
      const { data } = await api.get(`/sponsor-orgs/${s.id}`);
      setAssigned(new Set((data.assigned_users || []).map((u) => u.id)));
    } catch {
      setAssigned(new Set());
    }
  };

  const saveAssign = async () => {
    if (!assignOpen) return;
    setSubmitting(true);
    try {
      await api.put(`/sponsor-orgs/${assignOpen.id}/users`, { user_ids: Array.from(assigned) });
      toast.success("Membership updated");
      setAssignOpen(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="sponsor-orgs-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Sponsor organisations
          </h1>
          <p className="text-[#525C58] mt-1">External sending sponsors (e.g. Samaritan's Purse, World Medical Mission). Used on requests, bookings, and dashboard filters.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-[#525C58]" data-testid="show-inactive-toggle">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} /> Show inactive
          </label>
          {canManage && (
            <Button onClick={openCreate} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-sponsor-btn">
              <PlusCircle className="h-4 w-4 mr-2" /> New sponsor org
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#7A8A84]">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="border-[#E8E8E6]">
          <CardContent className="py-16 text-center text-[#7A8A84]">
            <Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No sponsor organisations yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s) => (
            <Card key={s.id} className={`border-[#E8E8E6] ${!s.active ? "opacity-60" : ""}`} data-testid={`sponsor-card-${s.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {s.logo_url
                      ? <img src={s.logo_url} alt="" className="w-10 h-10 rounded object-cover bg-[#F4F4F1]" />
                      : <div className="w-10 h-10 rounded bg-[#1B4332]/10 flex items-center justify-center text-[#1B4332] font-semibold">{s.name?.[0] || "?"}</div>
                    }
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1C1F1E] truncate">{s.name}</h3>
                      {!s.active && <Badge variant="outline" className="bg-gray-100 text-gray-700">Inactive</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canAssign && (
                      <Button size="icon" variant="ghost" onClick={() => openAssign(s)} title="Assign users" data-testid={`assign-users-${s.id}`}>
                        <Users className="h-4 w-4" />
                      </Button>
                    )}
                    {canManage && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(s)} title="Edit" data-testid={`edit-sponsor-${s.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(s)} title="Delete" className="text-red-600 hover:bg-red-50" data-testid={`delete-sponsor-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-xs text-[#525C58] space-y-1">
                  {s.contact_email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {s.contact_email}</div>}
                  {s.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {s.contact_phone}</div>}
                  {s.address && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {s.address}</div>}
                </div>
                {s.notes && <p className="text-xs text-[#7A8A84] italic">{s.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit sponsor org" : "New sponsor org"}</DialogTitle>
            <DialogDescription>External sending organisation that sponsors visitors.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="sponsor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact email</Label><Input type="email" data-testid="sponsor-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label>Contact phone</Label><Input data-testid="sponsor-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={save} disabled={submitting} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-sponsor-btn">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign users dialog */}
      <Dialog open={!!assignOpen} onOpenChange={(o) => !o && setAssignOpen(null)}>
        <DialogContent data-testid="assign-users-dialog">
          <DialogHeader>
            <DialogTitle>Assign users to "{assignOpen?.name}"</DialogTitle>
            <DialogDescription>Selected users will see requests/bookings tagged with this sponsor on their dashboards.</DialogDescription>
          </DialogHeader>
          <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-80 overflow-y-auto space-y-1.5">
            {users.length === 0 && <p className="text-sm text-[#7A8A84]">No users in this org.</p>}
            {users.map((u) => {
              const checked = assigned.has(u.id);
              return (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = new Set(assigned);
                      if (checked) next.delete(u.id); else next.add(u.id);
                      setAssigned(next);
                    }}
                    data-testid={`assign-user-${u.id}`}
                  />
                  <span className="truncate"><strong>{u.name}</strong> <span className="text-[#7A8A84]">· {u.role} · {u.email}</span></span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={saveAssign} disabled={submitting} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-assignment-btn">
              {submitting ? "Saving…" : `Save (${assigned.size} selected)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
